import './arrow-scroller.css';

import { IArrowScroller, IArrowScrollerProps } from './arrow-scroller.interface';

/**
 * A scroll controller component that adds arrow buttons to scroll horizontally or vertically.
 * Useful for overflowing containers such as tab bars or pill bars.
 */
export class ArrowScroller {
    private readonly _container: HTMLElement;
    private readonly _scrollTarget: HTMLElement;
    private readonly _props: IArrowScroller;
    private readonly _prevArrow: HTMLElement;
    private readonly _nextArrow: HTMLElement;

    /**
     * Initializes a new instance of the ArrowScroller.
     * @param {HTMLElement} scrollTarget - The scrollable element to attach arrows to.
     * @param {IArrowScrollerProps} [props] - Optional configuration for direction, color, and scroll amount.
     */
    constructor(scrollTarget: HTMLElement, props?: IArrowScrollerProps) {
        this._scrollTarget = scrollTarget;
        this._props = this._createProps(props);

        this._container = this._wrapContainer();
        this._prevArrow = this._createArrow("prev");
        this._nextArrow = this._createArrow("next");

        this._insertElements();
        this._bindEvents();
        this._updateIcons();
    }

    /**
     * Builds a complete IArrowScroller config object using defaults for any missing values.
     * @param {IArrowScrollerProps} [props] - Optional partial configuration.
     * @returns {IArrowScroller} - Fully constructed scroll configuration.
     * @private
     */
    private _createProps(props?: IArrowScrollerProps): IArrowScroller {
        return {
            direction: props?.direction ?? "horizontal",
            color: props?.color ?? "#FFF",
            scrollAmount: props?.scrollAmount ?? 300,
            localizationService: props?.localizationService
        };
    }

    /**
     * Gets the root container element that includes arrows and the scroll target.
     * @returns {HTMLElement} - The wrapped scroll container with controls.
     */
    public get element(): HTMLElement {
        return this._container;
    }

    /**
     * Wraps the scroll target in a container element for layout and arrow control injection.
     * @returns {HTMLElement} - The container wrapping the scrollable content.
     * @private
     */
    private _wrapContainer(): HTMLElement {
        const wrapper = document.createElement("div");
        wrapper.classList.add("arrow-scroller-container", this._props.direction);
        this._scrollTarget.parentElement?.insertBefore(wrapper, this._scrollTarget);
        wrapper.appendChild(this._scrollTarget);
        return wrapper;
    }

    /**
     * Creates an arrow button element.
     * @param {"prev" | "next"} type - Indicates whether it's the left/up or right/down arrow.
     * @returns {HTMLElement} - The arrow element.
     * @private
     */
    private _createArrow(type: "prev" | "next"): HTMLElement {
        const button = document.createElement("button");
        button.type = "button";
        button.classList.add("arrow-scroll", type, "hidden");

        const label = this._arrowLabel(type);
        if (label)
            button.setAttribute("aria-label", label);

        const span = document.createElement("span");
        span.classList.add("icon-arrow-back", `${type}-arrow`);
        span.setAttribute("aria-hidden", "true");
        button.appendChild(span);

        return button;
    }

    private _arrowLabel(type: "prev" | "next"): string {
        const keys = {
            horizontal: { prev: "ARIA_SCROLL_LEFT", next: "ARIA_SCROLL_RIGHT" },
            vertical: { prev: "ARIA_SCROLL_UP", next: "ARIA_SCROLL_DOWN" },
        };
        const key = keys[this._props.direction][type];

        return this._props.localizationService?.names(key) ?? "";
    }

    /**
     * Inserts the arrow buttons into the scroll container in the correct order.
     * Handles both horizontal and vertical directions.
     * @private
     */
    private _insertElements(): void {
        if (this._props.direction === "horizontal") {
            this._container.insertBefore(this._prevArrow, this._scrollTarget);
            this._container.appendChild(this._nextArrow);
        } else {
            this._container.prepend(this._prevArrow);
            this._container.appendChild(this._nextArrow);
            this._container.style.display = "flex";
            this._container.style.flexDirection = "column";
        }
    }

    /**
     * Binds scroll, click, and resize events to handle user interactions and arrow visibility.
     * @private
     */
    private _bindEvents(): void {
        this._scrollTarget.addEventListener("scroll", () => this._updateIcons());

        this._prevArrow.addEventListener("click", () => {
            this._scrollBy(-this._props.scrollAmount);
        });

        this._nextArrow.addEventListener("click", () => {
            this._scrollBy(this._props.scrollAmount);
        });

        let resizeTimeout: number;
        window.addEventListener("resize", () => {
            clearTimeout(resizeTimeout);
            resizeTimeout = window.setTimeout(() => this._updateIcons(), 100);
        });
    }

    /**
     * Scrolls the target element by the configured amount.
     * Uses smooth scrolling behavior.
     * @param {number} amount - The amount to scroll by in pixels (positive or negative).
     * @private
     */
    private readonly _scrollBy = (amount: number): void => {
        if (this._props.direction === "horizontal")
            this._scrollTarget.scrollBy({ left: amount, behavior: "smooth" });
        else
            this._scrollTarget.scrollBy({ top: amount, behavior: "smooth" });
    }

    /**
     * Updates the visibility of the scroll arrows based on scroll position.
     * Hides the arrows when at the start or end of scrollable content.
     * @private
     */
    private readonly _updateIcons = (): void => {
        const { scrollTop, scrollLeft, scrollHeight, scrollWidth, clientHeight, clientWidth } = this._scrollTarget;

        if (this._props.direction === "horizontal") {
            const scrollRight = scrollWidth - clientWidth - scrollLeft;
            this._prevArrow.classList.toggle("hidden", scrollLeft <= 1);
            this._nextArrow.classList.toggle("hidden", scrollRight <= 1);
        } else {
            const scrollBottom = scrollHeight - clientHeight - scrollTop;
            this._prevArrow.classList.toggle("hidden", scrollTop <= 1);
            this._nextArrow.classList.toggle("hidden", scrollBottom <= 1);
        }
    }

    /**
     * Public method to re-evaluate arrow visibility.
     * Useful when scrollable content changes dynamically.
     */
    public update(): void {
        setTimeout(() => {
            this._updateIcons();
        }, 100);
    }
}